/**
 * The RVTM: every requirement against the four verification methods.
 *
 * Two sources, and both of them deliberate.
 *
 * `GET /api/rtm` already carries what the rows need - `verification_methods`
 * is the requirement's NOMINAL intent and `verified_by` is the cases that
 * cover it - so this screen reads the same report the program is held to and
 * the same one the CSV exports, rather than adding a fetch per requirement for
 * a metadata list the uniform listing leaves out. What the RTM does NOT carry
 * is each case's own method, so `GET /api/verification` supplies that, once,
 * for every case at a time.
 *
 * The thing this screen must not do is present an inference as a fact.
 * NOTHING in the model sets a case's `method` today, so almost every case is
 * "verified by something, method not stated" - which is a completely different
 * statement from "nothing verifies this". Those two are rendered differently,
 * in different columns, and the legend says which is which.
 *
 * The red case follows the back end's own rule rather than a stricter one:
 * `repository.validate` raises `method-mismatch` only when the requirement
 * nominates SOMETHING and the case's method is not among it. A requirement
 * that nominates nothing cannot be contradicted, so a case under one is marked
 * as unnominated, not as a mismatch. Painting that red would be this screen
 * inventing a finding the model does not support.
 */

import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import { DataGrid } from '@mui/x-data-grid'
import type { GridColDef } from '@mui/x-data-grid'
import { ApiError, PAGE_LIMIT, api } from '../api'
import type { VerificationRow } from '../schema'
import { MONOSPACE } from '../theme'
import EmptyState from '../components/common/EmptyState'
import HelpTip from '../components/common/HelpTip'
import {
  WRAPPING_GRID_PROPS,
  WRAPPING_GRID_SX,
  useFittedHeight,
} from '../components/common/grid-layout'
import { naturalComparator } from '../components/common/naturalCompare'
import EntityFilterBar from '../components/entity/EntityFilterBar'
import ExportMenu from '../components/entity/ExportMenu'
import QuickFilters from '../components/entity/QuickFilters'
import { applyQuick, hasQuick, readQuick, writeQuick } from '../components/entity/quickFilter'
import type { QuickSelection } from '../components/entity/quickFilter'
import { matchesQuery, readSearch, writeSearch } from '../components/entity/rowFilter'
import { useRememberedFilters } from '../components/entity/rememberedFilters'
import { CELL_LABEL, coverageCaveat, matrixCsv, matrixFileName } from './matrixCsv'

/**
 * What one requirement/method cell says.
 *
 * Five states, not three: the two extra ones are the whole point of the
 * screen. `unattributed` is a case verifying by a method the requirement never
 * spoke about either way, which the back end explicitly does not treat as a
 * mismatch; `none` is genuinely nothing on either side.
 */
export type CellKind = 'covered' | 'gap' | 'mismatch' | 'unattributed' | 'none'

export interface MatrixCell {
  method: string
  kind: CellKind
  /** The cases that verify this requirement BY this method. */
  cases: string[]
}

export interface MatrixRow {
  id: string
  level: string
  subsystem: string
  segment: string
  status: string
  statement: string
  /** Methods the requirement nominates, from `verification_methods`. */
  nominated: string[]
  /** Every case that verifies it, whatever method it states. */
  cases: string[]
  /** Cases that verify it and state NO method - not the same as no case. */
  unstated: string[]
  cells: MatrixCell[]
}

/**
 * Split one of the RTM's comma-joined columns.
 *
 * The empty string has to answer an EMPTY list. `''.split(', ')` answers
 * `['']`, which reads downstream as a requirement that nominates one method
 * called "" - and then every blank cell in its row becomes a mismatch.
 */
export function splitList(text: string): string[] {
  return String(text ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
}

/**
 * What one cell of the matrix means.
 *
 * The whole colour scheme of this screen is this function, which is why it is
 * exported and tested on its own: every branch below renders as a plausible
 * coloured box, so a wrong one is invisible on screen and obvious here.
 *
 * @param nominated the methods the requirement states
 * @param method the column
 * @param cases the cases verifying this requirement BY this method
 */
export function classifyCell(nominated: string[], method: string, cases: string[]): CellKind {
  const states = nominated.includes(method)
  if (cases.length > 0) {
    if (states) return 'covered'
    // Faithful to repository.validate: 'if not nominal ... continue'. With
    // nothing nominated there is no disagreement to report.
    return nominated.length > 0 ? 'mismatch' : 'unattributed'
  }
  return states ? 'gap' : 'none'
}

/**
 * Build the matrix from the RTM's rows and each case's own method.
 *
 * @param rtmRows rows exactly as `GET /api/rtm` answers them
 * @param methods the columns, from the program's own VerificationMethodKind
 * @param caseMethods case identifier to the method it states, '' when unstated
 */
export function buildMatrix(
  rtmRows: Record<string, string>[],
  methods: string[],
  caseMethods: Map<string, string>,
): MatrixRow[] {
  return rtmRows.map((row) => {
    const nominated = splitList(row.verification_methods)
    const cases = splitList(row.verified_by)

    // A case this listing knows nothing about is treated as stating no method
    // rather than being dropped: it still verifies the requirement, and
    // silently losing it would understate the coverage.
    const unstated = cases.filter((identifier) => !caseMethods.get(identifier))

    const cells = methods.map((method) => {
      const byMethod = cases.filter((identifier) => caseMethods.get(identifier) === method)
      return { method, kind: classifyCell(nominated, method, byMethod), cases: byMethod }
    })

    return {
      id: row.id,
      level: row.level ?? '',
      subsystem: row.subsystem ?? '',
      segment: row.segment ?? '',
      status: row.status ?? '',
      statement: row.statement ?? '',
      nominated,
      cases,
      unstated,
      cells,
    }
  })
}

/** How each cell state is drawn and described. */
const CELL_STYLE: Record<
  CellKind,
  { colour: 'success' | 'warning' | 'error' | 'info' | null; label: string }
> = {
  covered: { colour: 'success', label: CELL_LABEL.covered },
  gap: { colour: 'warning', label: CELL_LABEL.gap },
  mismatch: { colour: 'error', label: CELL_LABEL.mismatch },
  unattributed: { colour: 'info', label: CELL_LABEL.unattributed },
  none: { colour: null, label: CELL_LABEL.none },
}

/** The quick filters the screen offers, in the order the bar draws them. */
const QUICK_FIELDS = [
  { name: 'level', label: 'Level' },
  { name: 'subsystem', label: 'Subsystem' },
  { name: 'segment', label: 'Segment' },
]

/**
 * `GET /api/rtm` as JSON.
 *
 * Through the client, which carries `X-BEST-Project`. A bare fetch reads
 * whichever program the SERVER last resolved rather than the one the picker is
 * showing - a traceability matrix quietly listing another program's rows is the
 * worst possible way to discover that.
 */
interface Rtm {
  columns: string[]
  rows: Record<string, string>[]
}

const fetchRtm = (): Promise<Rtm> => api.rtm()

/** The rows a filter keeps. */
type Only = 'all' | 'gap' | 'mismatch' | 'unverified' | 'unstated'

/** Whether a row satisfies one of the interesting-rows filters. */
function matchesOnly(row: MatrixRow, only: Only): boolean {
  switch (only) {
    case 'gap':
      return row.cells.some((cell) => cell.kind === 'gap')
    case 'mismatch':
      return row.cells.some((cell) => cell.kind === 'mismatch')
    case 'unverified':
      return row.cases.length === 0
    case 'unstated':
      return row.unstated.length > 0
    default:
      return true
  }
}

export default function VerificationTraceability() {
  // A column per verification method plus the requirement's own columns, so
  // the matrix runs off the right of the window on any real program. See
  // components/common/grid-layout.
  const { ref, height } = useFittedHeight()
  const [rtm, setRtm] = useState<Rtm | null>(null)
  const [methods, setMethods] = useState<string[] | null>(null)
  const [cases, setCases] = useState<VerificationRow[] | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  /**
   * The search, in the URL under `search` as on every other list (issue #86).
   *
   * It was component state, so it was gone the moment anything else was opened
   * and the box was empty on the way back. In the URL it is a link, and
   * remembered so that the sidebar and the breadcrumbs put it back. Replace, not
   * push: typing must not fill up the back button.
   */
  const [params, setParams] = useSearchParams()
  const search = readSearch(params)
  const setSearch = (next: string) =>
    setParams((now) => writeSearch(now, next), { replace: true })
  useRememberedFilters()
  /**
   * Entries the server holds in the verification listing, cases and plans alike,
   * against the ones it was allowed to send. The cap is `caseTotal > cases.length`:
   * the total counts plans too, so comparing it with the cases alone would warn
   * about a listing that was read in full.
   */
  const [caseTotal, setCaseTotal] = useState(0)
  const [only, setOnly] = useState<Only>('all')
  /**
   * The quick filters (level, subsystem, segment), in the URL under `quick` as on
   * Requirements, so "L3-L4 Prop" is a link that can be sent to someone.
   */
  const quick = useMemo(() => readQuick(params), [params])
  const changeQuick = (next: QuickSelection) =>
    setParams((now) => writeQuick(now, next), { replace: true })

  useEffect(() => {
    // The four method values come from the program's own enum, not from a list
    // written here: a program may specialize VerificationMethodKind, and a
    // hard-coded column set would then be quietly wrong.
    Promise.all([
      fetchRtm(),
      api.choices('verification', 'method'),
      // Every case: the filter below runs in the browser, so a capped fetch
      // would search only what was fetched. See VerificationFlowdown.
      api.verification({ limit: PAGE_LIMIT }),
    ])
      .then(([report, choices, page]) => {
        const rows = page.items
        setCaseTotal(page.total)
        setRtm(report)
        setMethods(choices)
        setCases(rows)
      })
      .catch((failure: ApiError) => setError(failure))
  }, [])

  /** Case identifier to the method it states. '' is "states none". */
  const caseMethods = useMemo(() => {
    const found = new Map<string, string>()
    for (const row of cases ?? []) {
      if (row.verification_kind !== 'case' || !row.id) continue
      found.set(row.id, String(row.method ?? ''))
    }
    return found
  }, [cases])

  const matrix = useMemo(
    () => (rtm && methods ? buildMatrix(rtm.rows, methods, caseMethods) : []),
    [rtm, methods, caseMethods],
  )

  /**
   * The search, then the quick filters, then the Show menu: the order every list
   * runs in. The quick filters count over the search alone (see QuickFilters).
   */
  const searched = useMemo(
    // Any column, shared term rule. The hand-listed set here omitted every
    // field the row might gain, which is the staleness rowFilter exists for.
    () => matrix.filter((row) => matchesQuery(row as unknown as Record<string, unknown>, search)),
    [matrix, search],
  )
  const narrowed = useMemo(() => applyQuick(searched, quick), [searched, quick])
  const shown = useMemo(() => narrowed.filter((row) => matchesOnly(row, only)), [narrowed, only])
  /** Whether anything narrows the rows, so the file and the count say so. */
  const filtered = search.trim().length > 0 || hasQuick(quick) || only !== 'all'

  /** How many cases state a method at all - the fact behind the legend. */
  const stated = useMemo(
    () => [...caseMethods.values()].filter((method) => method !== '').length,
    [caseMethods],
  )

  const columns = useMemo<GridColDef<MatrixRow>[]>(() => {
    const identity: GridColDef<MatrixRow>[] = [
      {
        field: 'id',
        headerName: 'Requirement',
        minWidth: 130,
        // Natural order, so SUB_2 comes before SUB_10 (issue #82).
        sortComparator: naturalComparator,
        renderCell: (params) => (
          <RouterLink
            to={`/requirements/${encodeURIComponent(params.row.id)}`}
            style={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}
          >
            {params.row.id}
          </RouterLink>
        ),
      },
      { field: 'level', headerName: 'Level', width: 80 },
      { field: 'subsystem', headerName: 'Subsystem', width: 110, sortComparator: naturalComparator },
      { field: 'status', headerName: 'Status', width: 90 },
    ]

    const perMethod: GridColDef<MatrixRow>[] = (methods ?? []).map((method) => ({
      field: method,
      headerName: method,
      width: 130,
      sortable: true,
      // The cell reads 'covered TC_2 TC_10': case ids inside, so natural.
      sortComparator: naturalComparator,
      valueGetter: (_value, row) => {
        const cell = row.cells.find((entry) => entry.method === method)
        return cell ? `${cell.kind} ${cell.cases.join(' ')}`.trim() : ''
      },
      renderCell: (params) => {
        const cell = params.row.cells.find((entry) => entry.method === method)
        if (!cell) return null
        return <Cell cell={cell} />
      },
    }))

    const unstated: GridColDef<MatrixRow>[] = [
      {
        field: 'unstated',
        headerName: 'No method stated',
        width: 170,
        sortComparator: naturalComparator,
        valueGetter: (_value, row) =>
          row.unstated.length ? row.unstated.join(' ') : row.cases.length ? '' : 'no case',
        renderCell: (params) => {
          const row = params.row
          if (row.unstated.length > 0) {
            return (
              <Box
                aria-label={`${row.unstated.join(', ')} verifies ${row.id} and states no method`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                  px: 0.75,
                  fontFamily: MONOSPACE,
                  fontSize: '0.78rem',
                  border: '1px dashed',
                  borderColor: 'text.disabled',
                  borderRadius: 1,
                }}
              >
                {row.unstated.join(', ')}
              </Box>
            )
          }
          if (row.cases.length === 0) {
            return (
              <Typography
                variant="caption"
                color="text.disabled"
                aria-label={`nothing verifies ${row.id}`}
                sx={{ fontStyle: 'italic' }}
              >
                no case
              </Typography>
            )
          }
          return null
        },
      },
    ]

    const statement: GridColDef<MatrixRow>[] = [
      { field: 'statement', headerName: 'Statement', flex: 3, minWidth: 260 },
    ]

    return [...identity, ...perMethod, ...unstated, ...statement]
  }, [methods])

  if (error) return <Alert severity="error">{error.message}</Alert>
  if (!rtm || !methods || !cases) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  /**
   * The matrix the export menu writes: the whole program, or the rows on screen.
   * The caveat goes into the file exactly when the screen warns about it.
   */
  const caveat = caseTotal > cases.length ? coverageCaveat(cases.length, caseTotal) : null
  const matrixFile = (scope: 'all' | 'shown') => ({
    csv: matrixCsv(scope === 'all' ? matrix : shown, methods, caveat),
    name: scope === 'all' ? 'rvtm.csv' : matrixFileName(quick, filtered),
  })

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h5">Verification traceability</Typography>
            {/* A RULE rather than a definition, so it is kept - a reader who does
                not know that intent and practice are allowed to differ will read
                every difference as an error. One hover, not four lines. */}
            <HelpTip
              label="How to read intent against practice"
              title={
                `Every requirement against the ${methods.length} verification methods this ` +
                'program’s enumeration defines. A requirement’s methods are its nominal ' +
                'intent, stated when it was written; a case’s method is what is actually ' +
                'done. They are allowed to differ, and a difference is reported rather ' +
                'than refused.'
              }
            />
          </Box>
        </Box>
        {/* The matrix is the file the screen draws, for the program or for what
            the filter shows; the RTM rows are the same requirements with their
            verification chain, for anyone who wants the chain columns. */}
        <ExportMenu
          plural="requirements"
          standard="rtm"
          matrixFile={matrixFile}
          shownIds={shown.map((row) => row.id)}
          filtered={filtered}
        />
      </Stack>

      <Alert severity={stated === 0 ? 'warning' : 'info'}>
        {stated === 0
          ? `No case in this program states a method, so nothing can land in the ${methods.length} ` +
            'method columns. Every case appears under “No method stated”, which is not the same ' +
            'as a requirement with no case at all — the legend below says which is which.'
          : `${stated} of ${caseMethods.size} cases state a method. The rest appear under ` +
            '“No method stated”, which is not the same as a requirement with no case at all.'}
      </Alert>

      <Paper sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Legend
        </Typography>
        <Stack spacing={0.75}>
          <Swatch kind="covered">
            The requirement nominates this method and a case verifies it by this method.
          </Swatch>
          <Swatch kind="gap">
            The requirement nominates this method and no case uses it. A gap in the plan, not in
            the tool.
          </Swatch>
          <Swatch kind="mismatch">
            A case verifies by this method while the requirement nominates others — the
            method-mismatch finding, raised at info severity. Both may be right; the point is
            that they disagree.
          </Swatch>
          <Swatch kind="unattributed">
            A case verifies by this method and the requirement nominates nothing at all. Not
            counted as a mismatch, because there is no stated intent to contradict.
          </Swatch>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 28,
                height: 18,
                border: '1px dashed',
                borderColor: 'text.disabled',
                borderRadius: 1,
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              <strong>No method stated</strong> — a case verifies the requirement but states no
              method of its own, so it belongs in no column.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box sx={{ width: 28, height: 18, flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">
              <strong>no case</strong> — nothing verifies this requirement at all. Different
              from the row above, and the one worth acting on.
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={2}>
        <Box sx={{ flexGrow: 1 }}>
          <EntityFilterBar plural="requirements" value={search} onChange={setSearch} />
          {caseTotal > cases.length && (
            <Typography variant="body2" color="warning.main">
              {coverageCaveat(cases.length, caseTotal)}
            </Typography>
          )}
        </Box>
        <TextField
          select
          value={only}
          onChange={(event) => setOnly(event.target.value as Only)}
          size="small"
          label="Show"
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="all">Every requirement</MenuItem>
          <MenuItem value="gap">A nominated method no case uses</MenuItem>
          <MenuItem value="mismatch">A case that disagrees with the nomination</MenuItem>
          <MenuItem value="unstated">A case that states no method</MenuItem>
          <MenuItem value="unverified">No case at all</MenuItem>
        </TextField>
      </Stack>

      <QuickFilters
        rows={matrix}
        counted={searched}
        fields={QUICK_FIELDS}
        value={quick}
        onChange={changeQuick}
      />
      {filtered && (
        <Typography variant="body2" color="text.secondary">
          Showing {shown.length} of {matrix.length} requirements.
        </Typography>
      )}

      <Paper sx={{ p: 1.5 }}>
        {shown.length === 0 ? (
          <EmptyState
            title={
              matrix.length === 0
                ? 'This program models no requirements'
                : 'Nothing matches that filter'
            }
            description={
              matrix.length === 0
                ? 'Import a model with requirements in it and this matrix fills itself in.'
                : 'Widen the filter, or clear the search.'
            }
          />
        ) : (
          <Box ref={ref} sx={{ height }}>
            <DataGrid
              {...WRAPPING_GRID_PROPS}
              rows={shown}
              columns={columns}
              getRowId={(row) => row.id}
              density="compact"
              disableRowSelectionOnClick
              initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
              pageSizeOptions={[25, 50, 100]}
              sx={WRAPPING_GRID_SX}
            />
          </Box>
        )}
      </Paper>
    </Stack>
  )
}

/** One coloured cell, with the case names in it and its meaning as a label. */
function Cell({ cell }: { cell: MatrixCell }) {
  const style = CELL_STYLE[cell.kind]
  if (cell.kind === 'none') return null

  return (
    <Box
      aria-label={`${cell.method}: ${style.label}${
        cell.cases.length ? ` (${cell.cases.join(', ')})` : ''
      }`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        px: 0.75,
        borderRadius: 1,
        fontFamily: cell.cases.length ? MONOSPACE : undefined,
        fontSize: '0.78rem',
        bgcolor: (theme) =>
          style.colour ? alpha(theme.palette[style.colour].main, 0.16) : 'transparent',
        // Colour alone is not a message. Every non-empty cell also carries
        // text, so the matrix survives a monochrome print and a screen reader.
        color: 'text.primary',
      }}
    >
      {cell.cases.length ? cell.cases.join(', ') : 'no case'}
    </Box>
  )
}

/** A legend line: the colour, then what it means. */
function Swatch({ kind, children }: { kind: CellKind; children: ReactNode }) {
  const style = CELL_STYLE[kind]
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <Box
        sx={{
          width: 28,
          height: 18,
          borderRadius: 1,
          flexShrink: 0,
          bgcolor: (theme) =>
            style.colour ? alpha(theme.palette[style.colour].main, 0.16) : 'transparent',
        }}
      />
      <Typography variant="caption" color="text.secondary">
        <strong>{kind}</strong> — {children}
      </Typography>
    </Stack>
  )
}
